import {
  FireflyButton,
  ChakraFlex,
  ModalV2,
  Text,
  FAIconName,
  useToastMessage,
  ActionBar,
} from '@equip.health/ui';

import { useUpdateDischargePlanStatusMutation } from '~/store/services/clinical-data/dischargePlan';
import { DischargePlanStatuses } from '../common/dischargePlanConstants';
import { DischargePlanEvents } from '~/analytics/dischargePlanEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

interface SignDischargePlanModalProps {
  dischargePlanId: string;
  isOpen: boolean;
  onClose: () => void;
  justification?: string;
}

const SignDischargePlanModal = ({
  dischargePlanId,
  isOpen,
  onClose,
  justification,
}: SignDischargePlanModalProps) => {
  const [
    updateDischargePlanStatus,
    { isLoading: isUpdateDischargePlanStatusLoading },
  ] = useUpdateDischargePlanStatusMutation();
  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();

  const handleSuccess = () => {
    createToast({
      icon: FAIconName.checkRegular,
      id: 'sign-discharge-plan-success',
      text: 'Successfully signed discharge plan',
      variant: 'success',
    });
  };

  const handleFailure = () => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: 'sign-discharge-plan-error',
      text: "We're sorry, we were unable to update the discharge plan status. Please try again later",
      variant: 'critical',
    });
  };

  return (
    <ModalV2
      footerContent={
        <ChakraFlex
          alignItems="center"
          justifyContent="space-between"
          marginTop="10px"
          width="100%"
        >
          <FireflyButton
            id="sign-discharge-plan-cancel"
            isLoading={isUpdateDischargePlanStatusLoading}
            leftIcon={FAIconName.timesRegular}
            onClick={() => {
              trackEvent(
                DischargePlanEvents.clickOnCancelButtonInSignDischargePlanModal,
              );
              onClose();
            }}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id="sign-discharge-plan-confirm"
            isLoading={isUpdateDischargePlanStatusLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={() => {
              trackEvent(
                DischargePlanEvents.clickOnSignDischargePlanButtonInSignDischargePlanModal,
              );
              updateDischargePlanStatus({
                dischargePlanId,
                statusCode: DischargePlanStatuses.SIGNED.code,
                signOffJustificationReason: justification,
              })
                .unwrap()
                .then(() => handleSuccess())
                .catch(() => handleFailure())
                .finally(() => onClose());
            }}
            variant="primary"
          >
            Sign discharge plan
          </FireflyButton>
        </ChakraFlex>
      }
      isOpen={isOpen}
      maxWidth="512px"
      minWidth="512px"
      onClose={() => {
        trackEvent(DischargePlanEvents.clickOnXIconInSignDischargePlanModal);
        onClose();
      }}
      title="Confirm patient is ready to discharge"
    >
      <ChakraFlex alignItems="center" flexDirection="column" gap="16px">
        <Text>
          I attest that the patient can now be discharged from Equip, and that
          the information in this discharge plan is accurate and complete to the
          best of my knowledge.
        </Text>
        <Text>
          Signing this plan will immediately create a task to discharge the
          patient.
        </Text>
        <ActionBar
          barIcon={FAIconName.exclamationTriangleRegular}
          id="sign-discharge-plan-warning"
          text="Upon discharge, all providers will be removed from the patient's care team and unable to message the patient."
          variant="warning"
        />
      </ChakraFlex>
    </ModalV2>
  );
};

export default SignDischargePlanModal;
